import { Location } from '../models';
import { apiClient } from './ApiClient';

const STORAGE_KEY = 'tide-analyzer.locations-cache';

/**
 * Service for managing a persistent cache of location data
 * Stores locations in localStorage for persistence across sessions
 */
class LocationCacheService {
  private locationCache: Map<number, Location>;
  
  constructor() {
    this.locationCache = new Map<number, Location>();
    this.loadFromStorage();
    console.log('LocationCacheService initialized');
  }

  /**
   * Load previously cached locations from localStorage
   */
  private loadFromStorage(): void {
    try {
      const storedData = localStorage.getItem(STORAGE_KEY);
      if (storedData) {
        const locations = JSON.parse(storedData) as Location[];
        locations.forEach(location => {
          this.locationCache.set(location.id, location);
        });
        console.log(`Loaded ${locations.length} locations from cache`);
      }
    } catch (error) {
      console.error('Failed to load locations from cache:', error);
      // Start with an empty cache if loading fails
    }
  }

  /**
   * Save current cache to localStorage
   */
  private saveToStorage(): void {
    try {
      const locations = Array.from(this.locationCache.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(locations));
    } catch (error) {
      console.error('Failed to save locations to cache:', error);
    }
  }

  /**
   * Check if a location ID exists in cache
   */
  has(locationId: number): boolean {
    return this.locationCache.has(locationId);
  }

  /**
   * Get location from cache
   * @throws Error if location not found
   */
  get(locationId: number): Location {
    const location = this.locationCache.get(locationId);
    if (!location) {
      throw new Error(`Location ${locationId} not found in cache`);
    }
    return location;
  }

  /**
   * Add or update a location in cache
   */
  set(location: Location): void {
    this.locationCache.set(location.id, location);
    this.saveToStorage();
  }

  /**
   * Get location name from cache with fallback
   */
  getLocationName(locationId: number, defaultName: string = "Unknown Location"): string {
    try {
      const location = this.get(locationId);
      return location.name;
    } catch (error) {
      return defaultName;
    }
  }

  /**
   * Fetch location if needed, with caching
   * Returns from cache if available, otherwise fetches from API
   */
  async fetchIfNeeded(locationId: number): Promise<Location | null> {
    // Check cache first
    if (this.has(locationId)) {
      return this.get(locationId);
    }
    
    // Not in cache, fetch from API
    try {
      const location = await apiClient.getLocationById(locationId);
      if (location) {
        this.set(location); // Add to cache
      }
      return location;
    } catch (error) {
      console.error(`Failed to fetch location ${locationId}:`, error);
      return null;
    }
  }

  /**
   * Clear the entire cache
   */
  clear(): void {
    this.locationCache.clear();
    localStorage.removeItem(STORAGE_KEY);
  }
}

// Export a singleton instance
export const locationCache = new LocationCacheService();
